import { adaptComputedPathOrUrl } from '@unifyapps/editor/components/SelectionMenu/adaptComputedPathOrUrl';
import { isNative } from '@unifyapps/ui/utils/isNative';
import { useCallback } from 'react';
import type { ActionHandlerType, OnActionArgs } from '../../../../context/ActionsProvider/context';
import { useGetNoCodeComputedData } from '../../../computeContext/useGetNoCodeComputedData';
import { useRouter } from '../../../useRouter';
import { SHOULD_NOT_PROPAGATE_UP } from '../../const';
import useLinking from './useLinking';

function getComputedPathAndSearchParams(path: string) {
  const index = path.includes('?') ? path.indexOf('?') : path.length;
  const urlSearch = path.slice(index);

  const searchParams = new URLSearchParams(urlSearch);
  const computedPath = path.slice(0, index);

  return { computedPath, searchParams };
}

export default function useNavigate() {
  const router = useRouter();
  const { getNoCodeComputedData } = useGetNoCodeComputedData();
  const { openLinkNewWindow, openLink } = useLinking();

  const navigate: ActionHandlerType = useCallback(
    ({ action, actionContext, domEvent }: OnActionArgs) => {
      const path = action.payload?.path as string | undefined;
      const { path: computedPath } = getNoCodeComputedData<{
        path: string | undefined;
      }>(
        {
          path,
        },
        actionContext,
      );

      const adaptedPath = adaptComputedPathOrUrl(computedPath?.trim());

      if (adaptedPath) {
        const { computedPath: computedPathWithoutSearchParams, searchParams } =
          getComputedPathAndSearchParams(adaptedPath);

        const windowSearchParams = new URLSearchParams(
          action.payload?.preserveSearchParams ? window.location.search : '',
        );

        const finalSearchParams = new URLSearchParams({
          ...Object.fromEntries(windowSearchParams),
          ...Object.fromEntries(searchParams),
        }).toString();

        const finalPath =
          finalSearchParams.length > 0 // final search params if empty, meaning no search params from either window or action
            ? `${computedPathWithoutSearchParams}?${finalSearchParams}`
            : computedPathWithoutSearchParams;

        if (isNative()) {
          if (action.payload?.target === '_blank') {
            openLinkNewWindow(finalPath, 'noreferrer noopener');
          } else {
            openLink(finalPath);
          }
          return Promise.resolve(SHOULD_NOT_PROPAGATE_UP);
        }

        if (action.payload?.target === '_blank') {
          openLinkNewWindow(finalPath, 'noreferrer noopener');
        } else if (action.payload?.history === 'replace') {
          domEvent?.stopPropagation?.();
          router.replace(finalPath, true);
        } else {
          domEvent?.stopPropagation?.();
          router.push(finalPath, true);
        }
      }
      return Promise.resolve(SHOULD_NOT_PROPAGATE_UP);
    },
    [getNoCodeComputedData, openLink, openLinkNewWindow, router],
  );

  return { navigate };
}
